/* eslint-disable import/no-cycle */ // TODO: {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4218}
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import log from 'common/ScalyrLogger';

import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';

import { ErrorResult } from 'hooks/useLRQ/useLRQ';
import { DashboardDistributionType } from 'components/dashboards/types';
import { getType } from 'components/dashboards/dashboardData';
import NoResults from 'components/dashboards/DashboardPage/NoResults';
import DistributionGraph from 'components/common/Graphs/DistributionGraph';

import { getLrqStatus, LRQ_STATUS } from 'common/util/graph';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import { ErrorEditGraphJSON } from './modals/EditGraphJSON';

/**
 * Crack open an LRQ Plot ErrorResult to obtain the error within.
 *
 * todo: suss out any other possible error syntaxes
 *
 * @param errorResult A potential LRQ error
 */
export const getRequestErrorMessage = (errorResult: ErrorResult): string => {
  if (errorResult.terminationReasons !== undefined) {
    const reasons = errorResult.terminationReasons as Array<string>;
    if (reasons.length > 0) {
      return reasons[0];
    }
  }
  return 'An unknown error has occurred.';
};

/**
 * Line, bar or area chart graph display for a dashboard.
 *
 * @prop config                 The config stanza for just this chart
 * @prop dataIds                The list of LRQ data ids to retreive data (only one for a power query)
 * @prop chartInstanceCallback  A callback to capture the Highcharts chart instance -- used for reszing
 */
const DashboardDistribution = ({
  index,
  config,
  dataIds,
  params,
}: DashboardDistributionType): JSX.Element => {

  const { results, errors } = useEventCache(dataIds);
  const vizType = getType(config);

  if (!dataIds || dataIds.length === 0) {
    return <></>;
  }

  const lrqStatus = getLrqStatus(results, errors, vizType.isTimeseries);
  if (lrqStatus === LRQ_STATUS.IN_PROGRESS) {
    return (<LoadingPlaceholder offsetTop={0} />);
  }

  if (lrqStatus === LRQ_STATUS.HAS_ERRORS) {
    const message = getRequestErrorMessage(errors.find((error) => error) as ErrorResult);
    const isSyntaxError = message.toLowerCase().includes('parsing');
    if (isSyntaxError) {
      return (<ErrorEditGraphJSON message={message} graphIndex={index} graphConfig={config} />);
    }
    return (<ErrorPlaceholder error={{ message }} />);
  }

  if (lrqStatus === LRQ_STATUS.NO_RESULTS) { // Not sure exactly what this case would be
    log.error(`Dashboard LRQ status of no results for: ${JSON.stringify(config)} dashboard config.`);
    return (<div className="DashboardGraph__NoResults DashboardGraph__NoResults--Error">No results returned, please try again.</div>);
  }

  if (lrqStatus === LRQ_STATUS.NO_MATCHES) {
    return <NoResults config={config} params={params} />;
  }

  const data = results?.[0]?.histogramData;

  const xValues = (data?.zeroCount !== undefined ? [1] : []).concat(data?.positiveXAxis || []);
  const yValues = (data?.zeroCount !== undefined ? [data.zeroCount] : []).concat(data?.positiveSampleCounts || []);
  return (
    <DistributionGraph
      className="DashboardGraph__Content"
      xValues={xValues}
      yValues={yValues}
    />
  );

};

export default DashboardDistribution;
